import { useEffect, useRef, useState } from "react";
import { CheckCheck, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ImportRow } from "./ImportRow";
import { FilePreview } from "./FilePreview";

// Order is fixed when rows first appear so cards don't jump while you edit.
const useStableOrder = (rows, needsAttention) => {
  const order = useRef([]);
  const known = new Set(order.current);
  const fresh = rows.filter((r) => !known.has(r.key) && r.phase !== "queued" && r.phase !== "reading");
  if (fresh.length) {
    const flagged = fresh.filter(needsAttention).map((r) => r.key);
    const ok = fresh.filter((r) => !needsAttention(r)).map((r) => r.key);
    const prevFlagged = order.current.filter((k) => rows.find((r) => r.key === k && needsAttention(r)));
    const prevRest = order.current.filter((k) => !prevFlagged.includes(k));
    order.current = [...prevFlagged, ...flagged, ...prevRest, ...ok];
  }
  const rank = new Map(order.current.map((k, i) => [k, i]));
  return [...rows].sort((a, b) => (rank.get(a.key) ?? 1e9) - (rank.get(b.key) ?? 1e9));
};

export const ReviewSheet = ({ open, onClose, bi, onCreated }) => {
  const [preview, setPreview] = useState(null);
  const { rows, pending, selected, creating } = bi;
  const ordered = useStableOrder(rows, bi.needsAttention);
  const reading = pending.filter((r) => r.phase === "queued" || r.phase === "reading").length;
  const attention = pending.filter((r) => r.phase === "ready" && bi.needsAttention(r)).length;
  const created = rows.length - pending.length;
  const canApproveAll = pending.some((r) => bi.selectable(r) && !r.checked);

  useEffect(() => {
    if (open && rows.length === 0) onClose();
  }, [open, rows.length, onClose]);

  const create = async () => {
    const { made } = await bi.createSelected();
    if (made) onCreated(made);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !creating && onClose()}>
      <DialogContent className="max-w-6xl w-[calc(100vw-1rem)] h-[92vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="import-review">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200 text-left">
          <DialogTitle className="text-xl" style={{ fontFamily: "Outfit" }}>Review ratecons</DialogTitle>
          <DialogDescription>
            High-confidence loads are already approved. Rows that need you are at the top. Only approved rows become loads.
          </DialogDescription>
          <div className="pt-3 flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-slate-900" data-testid="review-approved-count">{pending.length ? `${selected.length} of ${pending.length} approved` : `All ${created} load${created === 1 ? "" : "s"} created`}</span>
            {attention > 0 && <span className="pill border bg-amber-50 text-amber-800 border-amber-200" data-testid="review-attention-count">{attention} need attention</span>}
            {reading > 0 && <span className="inline-flex items-center gap-1 text-xs text-[#002FA7]" data-testid="review-reading-count"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {`Reading ${reading}\u2026`}</span>}
            {created > 0 && pending.length > 0 && <span className="text-xs text-emerald-700" data-testid="review-created-count">{created} created</span>}
            <div className="flex gap-2 ml-auto">
              {!pending.length ? (
                <Button size="sm" onClick={onClose} className="bg-emerald-600 hover:bg-emerald-700 rounded-full" data-testid="review-done-btn">Done</Button>
              ) : (<>
              <Button variant="outline" size="sm" className="rounded-full" disabled={!canApproveAll || creating} onClick={() => bi.selectAll(true)} data-testid="review-approve-all"><CheckCheck className="w-4 h-4 mr-1" /> Approve all</Button>
              <Button size="sm" onClick={create} disabled={!selected.length || creating} className="bg-[#002FA7] hover:bg-[#001A75] rounded-full" data-testid="review-create-btn">
                {creating && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                {creating ? "Creating\u2026" : `Create ${selected.length} load${selected.length === 1 ? "" : "s"}`}
              </Button>
              </>)}
            </div>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto bg-slate-50 p-4 space-y-3" data-testid="review-rows">
          {ordered.map((r) => (
            <ImportRow key={r.key} row={r} dup={bi.dupOf[r.key]} canSelect={!creating && bi.selectable(r)}
              onField={(k, v) => bi.setField(r.key, k, v)} onCheck={(v) => bi.setChecked(r.key, v)}
              onRemove={() => bi.remove(r.key)} onPreview={() => setPreview(r)} />
          ))}
        </div>
        <FilePreview row={preview} onClose={() => setPreview(null)} />
      </DialogContent>
    </Dialog>
  );
};
